import { useEffect, useState } from "react";
import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { starFeedback } from "../feedback";
import { celebratedStarDay, markStarCelebrated } from "../last-star";
import { ShootingStar } from "../components/ShootingStar";
import { Emoji } from "../components/Emoji";
import { StreakStrip } from "../components/StreakStrip";
import { KidAvatar } from "../components/KidAvatar";
import { GRID_DAYS, StarGrid } from "../components/StarGrid";
import { MILESTONE } from "../../shared/stars";
import { addDays, dayKey } from "../../shared/time";
import type { DayRecord, KidSummary } from "../../shared/types";
import { shortDate } from "../format";

/**
 * Which day a newly seen star is for. A past day closes by itself the first time anyone opens
 * Pointz after midnight, so a star can turn up the next afternoon, after the score has moved;
 * naming the day keeps it from reading as today's.
 */
function starDayText(day: string, today: string): string {
  if (day === today) return "for today";
  if (day === addDays(today, -1)) return "for yesterday";
  return `for ${shortDate(day)}`;
}

/** Every kid's stars, streak and badges, and their last four weeks. Children see both kids too. */
export function Rewards() {
  const { snapshot, loadDays } = useStore();
  // A kid with no entry yet (loading, or the read failed) gets no grid at all: a grid drawn
  // from nothing would show every day as "not closed yet".
  const [days, setDays] = useState<Record<string, DayRecord[]>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Kids with a star day this device has not celebrated yet, played one after another.
  const [toCelebrate, setToCelebrate] = useState<{ kid: KidSummary; day: string }[]>([]);
  const today = dayKey(Date.now());

  // Follows the snapshot, so a day closed on another phone appears once this one refreshes.
  useEffect(() => {
    if (!snapshot) return;
    let live = true;
    setLoading(true);
    const from = addDays(today, -(GRID_DAYS - 1));
    Promise.all(snapshot.kids.map(async (k) => [k.id, await loadDays(k.id, from, today)] as const))
      .then((entries) => {
        if (!live) return;
        setDays(Object.fromEntries(entries));
        setErr(null);
        setLoading(false);
        // A star a grown-up awarded on their phone reaches this one as a newer star day than the
        // last one celebrated here. Marked as soon as it is found, so the next load stays quiet.
        const fresh = snapshot.kids.flatMap((kid) => {
          const newest = entries.find(([id]) => id === kid.id)?.[1].filter((d) => d.star).at(-1)?.day;
          const seen = celebratedStarDay(kid.id);
          if (!newest || seen === null || newest <= seen) return [];
          markStarCelebrated(kid.id, newest);
          return [{ kid, day: newest }];
        });
        if (fresh.length > 0) setToCelebrate((queue) => [...queue, ...fresh]);
      })
      .catch(() => {
        if (!live) return;
        setDays({});
        setErr("Couldn't load the last four weeks.");
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [snapshot, loadDays, today]);

  const celebrating = toCelebrate[0];

  // Each queued kid gets the buzz and chime as their star starts.
  useEffect(() => {
    if (celebrating) starFeedback();
  }, [celebrating]);

  if (!snapshot) return null;

  return (
    <main className="screen">
      <h1 className="title">
        <Emoji>🏆</Emoji>Rewards
      </h1>
      {snapshot.kids.length === 0 && <p className="hint">Stars show up here once a kid has been added.</p>}
      {err && <p className="error">{err}</p>}
      {snapshot.kids.map((kid) => (
        <RewardCard key={kid.id} kid={kid} days={days[kid.id]} loading={loading} today={today} />
      ))}
      {celebrating && (
        <div style={kidStyle(celebrating.kid.colour)}>
          <ShootingStar
            key={celebrating.kid.id}
            text={`${celebrating.kid.name} got a star ${starDayText(celebrating.day, today)}!`}
            onDone={() => setToCelebrate((queue) => queue.slice(1))}
          />
        </div>
      )}
    </main>
  );
}

function streakText(streak: number): string {
  if (streak === 0) return "No streak yet";
  return `Streak ${streak} ${streak === 1 ? "day" : "days"}`;
}

function toNextBadge(streak: number): string {
  const into = streak % MILESTONE;
  if (streak > 0 && into === 0) return "7-day badge earned!";
  const left = MILESTONE - into;
  return `${left} more star ${left === 1 ? "day" : "days"} for a 7-day badge`;
}

function RewardCard({
  kid,
  days,
  loading,
  today,
}: {
  kid: KidSummary;
  days: DayRecord[] | undefined;
  loading: boolean;
  today: string;
}) {
  return (
    <section className="reward-card" style={kidStyle(kid.colour)} aria-label={kid.name}>
      <div className="reward-card__head">
        <div className="reward-card__who">
          <KidAvatar kid={kid} size="md" />
          <h2 className="reward-card__name">{kid.name}</h2>
        </div>
        <p className="reward-card__stars">
          <span className="reward-card__star" aria-hidden="true">
            ⭐
          </span>
          <span aria-hidden="true">{kid.stars}</span>
          <span className="visually-hidden">{`${kid.stars} ${kid.stars === 1 ? "star" : "stars"}`}</span>
        </p>
      </div>
      <div className="reward-card__body">
        <p className="reward-card__streak">
          <Emoji>🔥</Emoji>
          {streakText(kid.streak)}
        </p>
        <StreakStrip streak={kid.streak} />
        <p className="hint">{toNextBadge(kid.streak)}</p>
        <p className="reward-card__badges">
          <Emoji>🏅</Emoji>
          {kid.badges === 0
            ? "No 7-day badges yet"
            : `${kid.badges} × 7-day ${kid.badges === 1 ? "badge" : "badges"}`}
        </p>
        {days ? (
          <StarGrid days={days} today={today} firstDay={dayKey(kid.createdAt)} />
        ) : (
          loading && <p className="hint">Loading the last four weeks…</p>
        )}
      </div>
    </section>
  );
}
